import * as THREE from "three";
import { LevelSim, isAura } from "@highmarch/domain";
import { groundAt, roadLevelAt } from "./terrain";
import { hqTowerName, lib } from "./views";
import { BEACON, beaconAlpha, byTier, pulseArrival, pulseRadius } from "./statusFx";
import { reducedVfx } from "../ui/settings";
import { audio } from "../audio/audio";
import { beaconBuffs } from "../audio/cueRules";
import type { PooledVfx } from "./vfxPool";

/** The beacons' standing auras, drawn every frame by the VFX router (vfxRouter.ts `update`). */
export class BeaconAuras {
  constructor(private readonly vfx: PooledVfx, private readonly sim: LevelSim) {}

  /** Beacon aura state per beacon tower id: seconds to its next pulse, and the running pulse's age (Infinity: none). */
  private readonly auraOf = new Map<number, { next: number; pulse: number; seen: boolean }>();
  private auraTime = 0;
  private readonly auraAt = new THREE.Vector3();

  /**
   * The beacon never fires (an aura: towers.json fireIntervalTicks 0), so nothing used to show it at all. Each frame a
   * ring of ground motes turns at its range, and every `BEACON.pulseEvery` s a pulse runs out to the edge; as the pulse
   * passes a tower the sim has buffed (auraDamageMult above 1), that tower blinks gold.
   */
  update(dt: number): void {
    const sim = this.sim, reduced = reducedVfx();
    this.auraTime += dt;
    for (const a of this.auraOf.values()) a.seen = false;
    for (const t of sim.towerSlots) {
      if (!t.alive || !isAura(sim.balance.towers[t.archetype])) continue;
      let a = this.auraOf.get(t.id);
      if (!a) { a = { next: 0, pulse: Infinity, seen: true }; this.auraOf.set(t.id, a); }
      a.seen = true;
      const at = this.auraAt.set(t.cell.x, groundAt(t.cell.x, t.cell.y), t.cell.y);
      const radius = sim.rangeOf(t).toNumber();
      this.vfx.auraRing(at, radius, roadLevelAt, { count: Math.round(radius * BEACON.motesPerCell), time: this.auraTime, spin: reduced ? 0 : BEACON.spin, alpha: beaconAlpha(t.tier, this.auraTime, reduced), size: byTier(BEACON.size, t.tier), thickness: byTier(BEACON.thickness, t.tier) });
      if (!reduced && a.pulse < BEACON.pulseLife) {
        a.pulse += dt;
        const k = Math.min(1, a.pulse / BEACON.pulseLife);
        this.vfx.auraRing(at, pulseRadius(a.pulse, radius, BEACON.pulseLife), roadLevelAt, { count: BEACON.pulseMotes, time: this.auraTime, spin: 0, alpha: BEACON.pulseAlpha * (1 - k), size: BEACON.pulseSize, thickness: BEACON.pulseThickness });
      }
      if ((a.next -= dt) > 0) continue;
      a.next = BEACON.pulseEvery; a.pulse = 0;
      // The bowl rings with the pulse (A2), once the Beacon is lifting someone: an idle beacon's pulse is only seen.
      if (beaconBuffs(sim.towerSlots, t, radius)) audio.play("fire_beacon");
      if (reduced) continue;
      for (const o of sim.towerSlots) {
        if (!o.alive || o === t || o.auraDamageMult.toNumber() <= 1) continue;
        const d = Math.hypot(o.cell.x - t.cell.x, o.cell.y - t.cell.y);
        const base = new THREE.Vector3(o.cell.x, groundAt(o.cell.x, o.cell.y), o.cell.y);
        const oa = sim.balance.towers[o.archetype];
        const h = lib()?.height(hqTowerName(oa.id, oa.isBase, o.tier)) ?? 1;
        this.vfx.later(pulseArrival(d, radius, BEACON.pulseLife), () => this.vfx.buffMark(base, h));
      }
    }
    for (const [id, a] of this.auraOf) if (!a.seen) this.auraOf.delete(id);
  }

  /** Forgets every beacon (the level is going). */
  clear(): void { this.auraOf.clear(); }
}
